import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Paper,
  Divider
} from '@mui/material';
import {
  Print as PrintIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  AccountBalance as LoanIcon
} from '@mui/icons-material';

const PrintPreviewDialog = ({ open, loan, onClose, onPrint }) => {
  const [formData, setFormData] = useState({
    member_name: '',
    member_id_number: '',
    location: '',
    occupation: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Initialize form data when loan changes
  useEffect(() => {
    if (loan) {
      setFormData({
        member_name: loan.member?.name || '',
        member_id_number: loan.member?.id_number || '',
        location: loan.member?.location || '',
        occupation: loan.member?.occupation || ''
      });
      setErrors({});
    }
  }, [loan]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.member_name.trim()) {
      newErrors.member_name = 'Member name is required';
    }
    
    if (!formData.member_id_number.trim()) {
      newErrors.member_id_number = 'ID number is required';
    } else if (formData.member_id_number.length < 7) {
      newErrors.member_id_number = 'ID number must be at least 7 characters';
    }
    
    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }
    
    if (!formData.occupation) {
      newErrors.occupation = 'Occupation is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePrint = async () => {
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    try {
      // Get member_id - it's nested under loan.member.member_id
      const memberId = loan.member?.member_id;
      
      if (!memberId) {
        throw new Error('Member ID not found');
      }
      
      // Extract numeric part if ID is in format MEM/2025/0007
      const numericId = memberId.includes('/') 
        ? memberId.split('/').pop()  // Gets '0007' from 'MEM/2025/0007'
        : memberId;
      
      await onPrint({
        member_id: numericId,
        loan_id: loan.database_id,
        member_data: {
          name: formData.member_name.trim(),
          id_number: formData.member_id_number.trim(),
          location: formData.location.trim(),
          occupation: formData.occupation
        }
      });
    } catch (error) {
      console.error('Error printing:', error);
      alert(error.message || 'Failed to generate PDF');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `KSh ${parseFloat(amount || 0).toLocaleString()}`;
  };

  if (!loan) return null;

  return (
    <Dialog 
      open={open} 
      onClose={loading ? undefined : onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
        }
      }}
    >
      <DialogTitle sx={{ 
        borderBottom: '1px solid #e0e0e0',
        backgroundColor: '#f8f9fa',
        fontWeight: 'bold',
        fontSize: '1.25rem',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <PrintIcon />
        Print Loan Form - Verify Member Details
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          Please verify and update member information before generating the print form.
          Any changes will be saved to the member's profile.
        </Alert>

        {/* Member Information Section */}
        <Paper sx={{ p: 2, mb: 3, backgroundColor: 'background.default', border: 1, borderColor: 'divider' }}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <PersonIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
              Member Information
            </Typography>
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Member Name *"
                value={formData.member_name}
                onChange={(e) => handleChange('member_name', e.target.value)}
                error={!!errors.member_name}
                helperText={errors.member_name}
                placeholder="Enter full member name"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="ID Number *"
                value={formData.member_id_number}
                onChange={(e) => handleChange('member_id_number', e.target.value)}
                error={!!errors.member_id_number}
                helperText={errors.member_id_number}
                placeholder="Enter national ID number"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.occupation}>
                <InputLabel>Occupation *</InputLabel>
                <Select
                  value={formData.occupation}
                  onChange={(e) => handleChange('occupation', e.target.value)}
                  label="Occupation *"
                >
                  <MenuItem value="">Select occupation</MenuItem>
                  <MenuItem value="businessman">Businessman</MenuItem>
                  <MenuItem value="businesswoman">Businesswoman</MenuItem>
                </Select>
                {errors.occupation && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {errors.occupation}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Location *"
                value={formData.location}
                onChange={(e) => handleChange('location', e.target.value)}
                error={!!errors.location}
                helperText={errors.location}
                placeholder="Enter member location/address"
                InputProps={{
                  startAdornment: <LocationIcon color="action" sx={{ mr: 1 }} />
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Loan Details Section - READ ONLY */}
        <Paper sx={{ p: 2, backgroundColor: 'background.default', border: 1, borderColor: 'divider' }}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <LoanIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
              Loan Details
            </Typography>
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={{ backgroundColor: 'background.paper', p: 2, borderRadius: 1, border: 1, borderColor: 'divider' }}>
                <Typography variant="body2" color="textSecondary">Loan ID</Typography>
                <Typography variant="h6" color="primary">{loan.id}</Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ backgroundColor: 'background.paper', p: 2, borderRadius: 1, border: 1, borderColor: 'divider' }}>
                <Typography variant="body2" color="textSecondary">Group</Typography>
                <Typography variant="h6">{loan.group?.name || 'N/A'}</Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ backgroundColor: 'background.paper', p: 2, borderRadius: 1, border: 1, borderColor: 'divider' }}>
                <Typography variant="body2" color="textSecondary">Principal Amount</Typography>
                <Typography variant="h6" color="success.main">
                  {formatCurrency(loan.principalAmount)}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ backgroundColor: 'background.paper', p: 2, borderRadius: 1, border: 1, borderColor: 'divider' }}>
                <Typography variant="body2" color="textSecondary">Monthly Payment</Typography>
                <Typography variant="h6">
                  {formatCurrency(loan.monthlyRepayment)}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ backgroundColor: 'background.paper', p: 2, borderRadius: 1, border: 1, borderColor: 'divider' }}>
                <Typography variant="body2" color="textSecondary">Installments</Typography>
                <Typography variant="h6">{loan.installments} months</Typography>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Summary */}
          <Box sx={{ backgroundColor: 'background.paper', p: 2, borderRadius: 1, border: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" gutterBottom color="textSecondary">
              Loan Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2">Net Disbursement:</Typography>
                <Typography variant="h6" color="success.main">
                  {formatCurrency(loan.netDisbursementAmount)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">Total Repayment:</Typography>
                <Typography variant="h6">
                  {formatCurrency(loan.repaymentAmount)}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, borderTop: '1px solid #e0e0e0', backgroundColor: '#f8f9fa' }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          sx={{ minWidth: 100 }}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button 
          onClick={handlePrint}
          variant="contained" 
          color="primary"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PrintIcon />}
          sx={{ minWidth: 140 }}
          disabled={loading}
        >
          {loading ? 'Generating PDF...' : 'Generate & Print'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrintPreviewDialog;